import { memo, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useVirtualizer } from 'tanstack-virtual/react-virtual/index.js';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { ScrollOverhang } from 'foundations-components/transitional/components/ScrollOverhang';
import { TranscriptEmptyStateWrapper } from './TranscriptEmptyStateWrapper';
import { TranscriptSearchBar } from './TranscriptSearchBar';
import { TranscriptUtterance } from './TranscriptUtterance';
import { AutoScrollButton } from '../shared/AutoScrollButton';
import { ActiveUtteranceUpdater } from './ActiveUtteranceUpdater';
import { getUtteranceId } from '../../utils/idHelpers';
import { TranscriptContext } from '../../contexts/TranscriptContext';
import { TRANSCRIPT_UTTERANCES_CONTAINER_ID } from '../../constants/ContainerIds';
import { initialSearchState } from '../../types/TranscriptSearchStateInterface';
import { ADVANCED } from '../../types/TranscriptTypes';
import TranscriptFeedback from '../transcript-feedback/TranscriptFeedback';
import { MediaPlaybackControlsContext } from '../../media-player/context/MediaPlaybackControlsContext';
import CopyTranscriptButton from './CopyTranscriptButton';
import { StyledScrollableDiv } from '../shared/ScrollableDiv';
import { useFetchCommentsForEngagement } from '../../apis/CommentsApi';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MemoizedTranscript = /*#__PURE__*/memo(props => {
  const {
    onShareIconClick,
    onCommentIconClick,
    onCopyIconClick,
    isPaused,
    variant = ADVANCED,
    setMaxHeightForTranscriptUtterances = false,
    onCopyTranscriptClick,
    renderCommentButton,
    callId,
    showCommentViewerButton,
    belowSearchBarContent
  } = props;
  const {
    highlightedOrFullTranscript,
    matchedUtterance,
    searchState,
    autoScrollTranscript,
    setAutoScrollTranscript,
    selectedUtteranceId,
    setSelectedUtteranceId,
    selectedUtteranceIndex,
    setSelectedUtteranceIndex,
    onTranscriptAutoScrollClickCallback,
    handleSearch,
    firstUnmatchedUtteranceIdRef,
    setIsMatchingPopoverOpen
  } = useContext(TranscriptContext);
  const {
    data
  } = useFetchCommentsForEngagement(callId || '');
  const allComments = (data === null || data === void 0 ? void 0 : data.commentsForEngagement) || [];
  const scrollContainerRef = useRef(null);
  const virtualizer = useVirtualizer({
    count: highlightedOrFullTranscript.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 80,
    overscan: 5,
    scrollPaddingStart: 28,
    scrollPaddingEnd: 28
  });
  const firstUtteranceIndexBySpeaker = useMemo(() => {
    const map = new Map();
    highlightedOrFullTranscript.forEach((u, i) => {
      if (!map.has(u.speakerTrackId)) {
        map.set(u.speakerTrackId, i);
      }
    });
    return map;
  }, [highlightedOrFullTranscript]);
  const handleActiveUtteranceChange = useCallback(utteranceId => {
    const index = highlightedOrFullTranscript.findIndex(u => getUtteranceId(u, TRANSCRIPT_UTTERANCES_CONTAINER_ID) === utteranceId);
    if (index >= 0) {
      virtualizer.scrollToIndex(index, {
        align: 'start',
        behavior: 'smooth'
      });
      setSelectedUtteranceIndex(index);
    }
    setSelectedUtteranceId(utteranceId);
  }, [highlightedOrFullTranscript, virtualizer, setSelectedUtteranceId, setSelectedUtteranceIndex]);

  // Wait for the transcript tab to open before scrolling to the first unmatched utterance
  // from onMatchSpeakersLinkClick
  const linkCallback = useCallback(() => {
    if (firstUnmatchedUtteranceIdRef.current !== null) {
      setIsMatchingPopoverOpen(true);
      setAutoScrollTranscript(false);
      firstUnmatchedUtteranceIdRef.current = null;
    }
  }, [firstUnmatchedUtteranceIdRef, setIsMatchingPopoverOpen, setAutoScrollTranscript]);
  useEffect(() => {
    if (autoScrollTranscript && selectedUtteranceIndex >= 0 && !searchState.term) {
      virtualizer.scrollToIndex(selectedUtteranceIndex, {
        align: 'start',
        behavior: 'smooth'
      });
      linkCallback();
    }
  }, [autoScrollTranscript, selectedUtteranceIndex, searchState.term, linkCallback, virtualizer]);
  useEffect(() => {
    if (matchedUtterance && !!searchState.term) {
      setAutoScrollTranscript(false);
      const matchedIndex = highlightedOrFullTranscript.findIndex(u => u.id === matchedUtterance.id);
      if (matchedIndex >= 0) {
        virtualizer.scrollToIndex(matchedIndex, {
          align: 'start'
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.term, matchedUtterance, highlightedOrFullTranscript, virtualizer]);
  const handleManualScroll = () => {
    if (!isPaused && autoScrollTranscript) {
      setAutoScrollTranscript(false);
    }
  };
  const autoScrollButtonClickCallback = () => {
    setAutoScrollTranscript(true);
    if (onTranscriptAutoScrollClickCallback) {
      onTranscriptAutoScrollClickCallback();
    }
    if (searchState.term) {
      handleSearch(initialSearchState);
    }
  };
  return /*#__PURE__*/_jsx(TranscriptEmptyStateWrapper, {
    children: /*#__PURE__*/_jsxs(Flex, {
      "data-test-id": "transcriptContainer",
      alignItems: "stretch",
      flexDirection: "column",
      blockSize: "100%",
      children: [/*#__PURE__*/_jsxs(Box, {
        paddingBlockEnd: "400",
        children: [/*#__PURE__*/_jsx(TranscriptSearchBar, {}), belowSearchBarContent, /*#__PURE__*/_jsx(View, {
          paddingBlockStart: "300",
          children: /*#__PURE__*/_jsx(AutoScrollButton, {
            autoScroll: autoScrollTranscript,
            onClickCallback: autoScrollButtonClickCallback
          })
        })]
      }), /*#__PURE__*/_jsx(ActiveUtteranceUpdater, {
        containerId: TRANSCRIPT_UTTERANCES_CONTAINER_ID
      }), /*#__PURE__*/_jsx(ScrollOverhang, {
        gradientColor: "fill-surface-default",
        size: 28,
        children: /*#__PURE__*/_jsx(StyledScrollableDiv, {
          ref: scrollContainerRef,
          "data-test-id": "transcriptUtterances",
          id: TRANSCRIPT_UTTERANCES_CONTAINER_ID,
          onWheel: handleManualScroll,
          maxHeight: setMaxHeightForTranscriptUtterances ? '33vh' : undefined,
          children: /*#__PURE__*/_jsx("div", {
            "data-total-count": highlightedOrFullTranscript.length,
            style: {
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative'
            },
            children: virtualizer.getVirtualItems().map(virtualRow => {
              var _nextUtteranceData$st;
              const utteranceData = highlightedOrFullTranscript[virtualRow.index];
              const utteranceId = getUtteranceId(utteranceData, TRANSCRIPT_UTTERANCES_CONTAINER_ID);
              const isUtteranceSelected = selectedUtteranceId === utteranceId;
              const nextUtteranceData = highlightedOrFullTranscript[virtualRow.index + 1];
              const isFirstUtteranceForSpeaker = firstUtteranceIndexBySpeaker.get(utteranceData.speakerTrackId) === virtualRow.index;
              return /*#__PURE__*/_jsx("div", {
                "data-index": virtualRow.index,
                ref: virtualizer.measureElement,
                style: {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`
                },
                children: /*#__PURE__*/_jsx(TranscriptUtterance, {
                  utteranceData: utteranceData,
                  utteranceId: utteranceId,
                  isSelected: isUtteranceSelected,
                  utteranceScrollCallback: handleActiveUtteranceChange,
                  onShareIconClick: onShareIconClick,
                  onCommentIconClick: onCommentIconClick,
                  onCopyIconClick: onCopyIconClick,
                  renderCommentButton: renderCommentButton,
                  showAIIcon: isFirstUtteranceForSpeaker,
                  variant: variant,
                  engagementId: callId,
                  allComments: allComments,
                  showCommentViewerButton: showCommentViewerButton,
                  nextUtteranceStartTime: (_nextUtteranceData$st = nextUtteranceData === null || nextUtteranceData === void 0 ? void 0 : nextUtteranceData.startTimeSeconds) !== null && _nextUtteranceData$st !== void 0 ? _nextUtteranceData$st : utteranceData.endTimeSeconds
                })
              }, virtualRow.key);
            })
          })
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "center",
        children: [/*#__PURE__*/_jsx(TranscriptFeedback, {
          onTranscriptFeedback: props.onTranscriptFeedback,
          callId: props.callId,
          transcriptId: props.transcriptId,
          objectTypeId: props.objectTypeId
        }, props.callId), /*#__PURE__*/_jsx(CopyTranscriptButton, {
          onCopyTranscriptClick: onCopyTranscriptClick
        })]
      })]
    })
  });
});
export const Transcript = props => {
  const {
    isPaused
  } = useContext(MediaPlaybackControlsContext);
  return /*#__PURE__*/_jsx(MemoizedTranscript, Object.assign({}, props, {
    isPaused: isPaused
  }));
};